/**
 * components/legal/legalContent.ts
 *
 * The public legal documents: the general terms (VOP) and their
 * specification, the price list, the recurring terms, the complaints
 * procedure, contact details, the personal-data notice and the processing
 * agreement. The VOP and the specification are counsel's and live in `vop/`,
 * as do the pages restating them; this file writes the rest and puts them in
 * one table. They exist because the payment gateway requires a
 * merchant site to publish them at stable, publicly reachable URLs before it
 * will enable card payments — the portal asks for the "URL of terms and
 * conditions" and the "URL of contact details" directly.
 *
 * ## Why the copy lives here and not in `i18n/`
 *
 * These are documents, not interface strings: several thousand words per
 * language, revised as legal text rather than as UI copy, and never
 * interpolated into a component. Keeping them out of `en.ts` / `cs.ts` keeps
 * those files about the product. The i18n rule that matters — **both
 * languages, always in step** — is enforced here instead by
 * `legalContent.test.ts`, which holds the Czech and English documents to the
 * same section ids in the same order, exactly as the shared `Translations`
 * type does for the interface strings.
 *
 * ## What the copy must keep saying
 *
 * The gateway checks for identification, a reachable e-mail and phone, the
 * final price including every fee, the accepted payment methods and card
 * security, how a digital service is delivered, withdrawal and complaint
 * handling, and a personal-data notice. The tests assert those facts are
 * present in both languages, so a rewrite cannot quietly drop one.
 *
 * The price list (`subscription`) and the processing agreement (`dpa`) are
 * the terms' Annexes 3 and 2, counsel's text (`vop/vopPriceList.ts`,
 * `dpaContent.ts`). The price list is a contract document counsel asked to
 * keep free of anything it does not say, so what the gateway reads beside a
 * price — how to pay, delivery, territorial reach — is a page of its own
 * (`payment`). Its amounts are built from `priceList.ts`, which
 * `priceList.parity.test.ts` holds to the engine that charges.
 *
 * The recurring terms state no amount — only that a year is twelve months at
 * the monthly price — and the general terms state none at all: the Fee is
 * the price list's, their Annex 3. The pages that restate the general terms
 * carry the terms' date (`vop/vopCompanions.ts`); the dates kept here date
 * the base they are derived from.
 *
 * The VAT wording assumes the provider is not a VAT payer, matching the
 * `BILLING_VAT_PAYER` default — flipping that switch means revisiting the
 * price list's clause 1.2.
 *
 * Every document names the company's official address, `COMPANY.email`, and
 * never the support mailbox, which belongs to the app's own prompts.
 */

import { COMPANY, formattedAddress } from "./companyDetails";
import { DPA_CS, DPA_EN } from "./dpaContent";
import { buildVopComplaints, buildVopContact, buildVopPayment, buildVopRecurring } from "./vop/vopCompanions";
import { VOP_PRICE_LIST_CS, VOP_PRICE_LIST_EN } from "./vop/vopPriceList";
import { VOP_SPECIFICATION_CS, VOP_SPECIFICATION_EN } from "./vop/vopSpecification";
import { VOP_TERMS_CS, VOP_TERMS_EN } from "./vop/vopTerms";

export type LegalLanguage = "cs" | "en";
/** The documents in force: what the public addresses serve and every surface links. */
export type LegalDocumentKey =
    /** The general terms (VOP), counsel's draft of 27 Oct 2026: `vop/vopTerms.ts`. */
    | "terms"
    /** Annex 1 of the terms: what the service does, by which a defect is judged. */
    | "specification"
    | "contact"
    | "privacy"
    /** Annex 3 of the terms: the price list (Ceník), counsel's text, `vop/vopPriceList.ts`. */
    | "subscription"
    /**
     * How to pay and how the service is delivered: the card schemes, the
     * gateway, 3-D Secure, delivery and territorial reach the payment gateway
     * reads on a merchant's site. A page of its own because the price list is
     * a contract document counsel asked to keep free of anything it does not
     * say (`vop/vopCompanions.ts`).
     */
    | "payment"
    /**
     * The "recurring payment terms". Deliberately a separate document from
     * `terms`: the gateway's form states the page "must not be identical to
     * the General Terms and Conditions", and a cardholder agreeing to
     * automatic debits deserves the mandate spelled out on its own page
     * rather than buried in article 5 of a long contract.
     */
    | "recurring"
    /**
     * The "reklamační řád" — how a defect or a wrong charge is reported, what
     * is warranted, and what is excluded. Its own document rather than an
     * article of the terms because the gateway's checklist asks for it by name
     * and expects a URL, and because the conditions that *exclude* a complaint
     * need the room to be listed rather than summarised. Note the name is a
     * document key here; `complaints` is separately a section id inside the
     * terms and the contact page, which point at this document.
     */
    | "complaints"
    /**
     * The Art. 28 processing agreement. Optivo reads its customers' guests out
     * of a PMS, which makes the hotel the controller of that data and Optivo
     * its processor — a relationship GDPR requires to rest on a written
     * contract. Annex 2 of the terms, counsel's text, in `dpaContent.ts`.
     */
    | "dpa";


/** A term a clause defines, set apart from its definition. */
export interface LegalDefinedTerm {
    term: string;
    text: string;
}

/**
 * A numbered clause of a contract. Counsel's documents are numbered clause by
 * clause ("11.4") and point by point under a clause ("11.4.1"), and refer to
 * one another by those numbers, so the numbers are drawn from the structure:
 * the article's from its heading, the clause's and the point's from their
 * place. A document that is prose under headings uses `paragraphs` instead.
 */
export interface LegalClause {
    text: string;
    /** The points under the clause. A definition names its term apart from its text. */
    items?: (string | LegalDefinedTerm)[];
    /** A sentence that closes the clause after its points, without a number of its own. */
    closing?: string;
    /**
     * Drawn in semibold. For a clause counsel set in bold on purpose: one a
     * business would not expect in standard terms has to stand out to bind.
     */
    emphasized?: boolean;
}

export interface LegalSection {
    /** Stable identity, shared by both languages. */
    id: string;
    heading: string;
    /** Rendered before the list — use for text that introduces it. */
    paragraphs?: string[];
    /**
     * A plain list. Items nested one level under an item went with the price
     * list of bands and plans they were made for (5 Oct 2026).
     */
    bullets?: string[];
    /** Numbered clauses, drawn after the list. See {@link LegalClause}. */
    clauses?: LegalClause[];
    /**
     * Rendered after the list. A caveat has to follow the figures it refers
     * to, and a single `paragraphs` field cannot express "this one comes
     * after".
     */
    notes?: string[];
    definitions?: { term: string; value: string }[];
}

export interface LegalDoc {
    title: string;
    /**
     * ISO date the wording took effect. The general terms and the pages that
     * restate them carry an open point here until their date is set
     * (`vop/effectiveDate.ts`).
     */
    effectiveDate: string;
    intro?: string[];
    sections: LegalSection[];
}

const EFFECTIVE_DATE = "2026-08-25";

/**
 * The recurring-payment terms' own date since 5 Oct 2026, the day they were
 * reworded for the price list. Their page restates the general terms now and
 * prints those terms' date (`vop/vopCompanions.ts`); this one dates the base
 * it is derived from.
 */
const RECURRING_EFFECTIVE_DATE = "2026-10-05";

/**
 * The privacy notice's own date since the Ask Optivo assistant: it names
 * Anthropic, which generates the assistant's answers, and took effect 30 days
 * after the e-mail notice of the new sub-processor. The service does not hold
 * the assistant back until that day (the hold was taken out on 8 Oct 2026, on
 * the owner's decision). The processing agreement naming Anthropic is now an
 * annex of the general terms and takes effect with them. The contact page's
 * base keeps `EFFECTIVE_DATE`.
 */
const PRIVACY_EFFECTIVE_DATE = "2026-11-05";

/**
 * The legal name closing a sentence: it already ends in a full stop
 * ("OPTIVO s.r.o."), so the sentence must not add a second one.
 */
const legalNameAtSentenceEnd = (): string =>
    COMPANY.legalName.endsWith(".") ? COMPANY.legalName : `${COMPANY.legalName}.`;

const dicLine = (lang: LegalLanguage): string =>
    COMPANY.dic
        ? `DIČ: ${COMPANY.dic}`
        : lang === "cs"
          ? "DIČ: poskytovatel není plátcem DPH"
          : "VAT ID: the provider is not registered for VAT";

/**
 * The documents written here rather than by counsel. The recurring terms and
 * the contact page are the base the VOP's companion pages restate
 * (`vop/vopCompanions.ts`); the privacy notice is read as it is.
 */
type OwnDocumentKey = "contact" | "privacy" | "recurring";

// ---------------------------------------------------------------------------
// Czech
// ---------------------------------------------------------------------------

const CS: Record<OwnDocumentKey, LegalDoc> = {

    contact: {
        title: "Kontaktní údaje",
        effectiveDate: EFFECTIVE_DATE,
        intro: [
            `Provozovatelem služby ${COMPANY.brandName} je společnost ${legalNameAtSentenceEnd()} Níže jsou uvedeny úplné identifikační a kontaktní údaje.`,
        ],
        sections: [
            {
                id: "identification",
                heading: "Identifikační údaje",
                definitions: [
                    { term: "Obchodní firma", value: COMPANY.legalName },
                    { term: "Sídlo", value: formattedAddress("cs") },
                    { term: "IČO", value: COMPANY.ico },
                    { term: "DIČ", value: dicLine("cs").replace("DIČ: ", "") },
                    { term: "Zápis v rejstříku", value: COMPANY.registration },
                    { term: "Web", value: COMPANY.website },
                ],
            },
            {
                id: "contact",
                heading: "Spojení",
                definitions: [
                    { term: "E-mail", value: COMPANY.email },
                    { term: "Telefon", value: COMPANY.phone },
                    { term: "Provozní doba", value: COMPANY.supportHoursCs },
                ],
                paragraphs: [
                    "Na e-mailové dotazy odpovídáme zpravidla do dvou pracovních dnů. Adresa sídla slouží zároveň jako korespondenční adresa pro reklamace a odstoupení od smlouvy.",
                ],
            },
            {
                id: "billing",
                heading: "Fakturační a bankovní spojení",
                definitions: [
                    { term: "Fakturační e-mail", value: COMPANY.billingEmail },
                    { term: "Banka", value: COMPANY.bankName },
                    { term: "Číslo účtu", value: COMPANY.bankAccount },
                    { term: "IBAN", value: COMPANY.iban },
                    { term: "BIC/SWIFT", value: COMPANY.bic },
                ],
                paragraphs: [
                    "Platbu převodem prosím označte variabilním symbolem, který aplikace uvádí u vystavené platby. Daňové doklady jsou dostupné ke stažení přímo v aplikaci.",
                ],
            },
            {
                id: "complaints",
                heading: "Reklamace a odstoupení",
                paragraphs: [
                    `Reklamace i odstoupení od smlouvy přijímáme na ${COMPANY.email} nebo písemně na adresu sídla. Reklamaci vyřídíme do 30 dnů. Úplný postup, včetně toho, co poskytovatel za službu odpovídá a kdy reklamaci uznat nelze, upravuje reklamační řád na /complaints.`,
                ],
            },
            {
                id: "documents",
                heading: "Související dokumenty",
                bullets: [
                    "Obchodní podmínky: /terms",
                    "Reklamační řád: /complaints",
                    "Zásady ochrany osobních údajů: /privacy",
                ],
            },
        ],
    },

    recurring: {
        title: "Podmínky opakovaných plateb",
        effectiveDate: RECURRING_EFFECTIVE_DATE,
        intro: [
            `Tento dokument popisuje, jak u služby ${COMPANY.brandName} vzniká souhlas s opakovanými platbami z karty, jak se platby strhávají a jak je zrušit. Doplňuje obchodní podmínky na /terms a týká se výhradně automatického obnovování předplatného.`,
        ],
        sections: [
            {
                id: "origination",
                heading: "1. Jak souhlas vzniká",
                paragraphs: [
                    "Opakované platby nevznikají automaticky. Vzniknou pouze tehdy, když při platbě kartou výslovně zaškrtnete volbu automatického obnovování. Bez zaškrtnutí proběhne jednorázová platba a nic dalšího se nestrhne.",
                    "První platba, kterou takto potvrdíte v platební bráně, je zároveň udělením souhlasu (mandátu) s dalšími platbami. Před jejím odesláním je v aplikaci uvedena částka, četnost i den další platby.",
                    "Souhlas uděluje držitel karty v prostředí platební brány GoPay. Číslo karty je uloženo u brány; poskytovatel k němu nemá přístup a neukládá je.",
                ],
            },
            {
                id: "amount",
                heading: "2. Částka a četnost",
                bullets: [
                    "Četnost: podle zvoleného období — u měsíčního předplatného jednou měsíčně, u ročního jednou za rok, vždy v den, kdy končí zaplacené období.",
                    "Roční předplatné: dvanáct měsíců zaplacených najednou, za stejnou měsíční cenu jako při měsíční platbě.",
                    "Měna: CZK.",
                ],
                notes: [
                    "Jde o platbu s proměnnou částkou: cena se odvíjí od objektů, které předplatné pokrývá, od počtu jejich pokojů a od individuálně sjednaných služeb, takže se mezi obdobími může změnit. Z toho důvodu je mandát u brány veden jako platba na vyžádání (on-demand), nikoli jako pevná trvalá platba.",
                    "Aktuální částka pro následující období je vždy zobrazena v aplikaci v sekci Předplatné dříve, než k jejímu stržení dojde.",
                ],
            },
            {
                id: "duration",
                heading: "3. Doba platnosti souhlasu",
                paragraphs: [
                    "Souhlas je udělen na dobu pěti let od první platby, nebo do dřívějšího zrušení. Po uplynutí této doby se platby zastaví samy a je třeba udělit souhlas znovu.",
                    "Souhlas zaniká také zrušením předplatného, expirací karty nebo jejím zablokováním u vydavatele.",
                ],
            },
            {
                id: "cancellation",
                heading: "4. Jak opakované platby zrušit",
                paragraphs: [
                    "Automatické obnovování můžete kdykoli zrušit v aplikaci v sekci Předplatné tlačítkem „Zrušit automatické obnovování“. Zrušení je okamžité a bezplatné — mandát je zneplatněn i na straně platební brány, takže z karty už nelze strhnout další platbu.",
                    `Zrušení lze uplatnit i e-mailem na ${COMPANY.email}; vyřídíme je nejpozději následující pracovní den.`,
                    "Zrušením automatického obnovování se neruší již zaplacené období — to doběhne do konce. Zruší se pouze budoucí automatické platby.",
                ],
            },
            {
                id: "failure",
                heading: "5. Neúspěšná platba",
                paragraphs: [
                    "Neprojde-li platba (nedostatek prostředků, expirovaná či zablokovaná karta), předplatné se neprodlouží a v aplikaci se zobrazí důvod neúspěchu. Mandát nerušíme — karta může fungovat druhý den — ale automaticky ani opakovaně se nepokoušíme strhávat částku bez vašeho vědomí.",
                    "Zaplacené období doběhne do konce. Nedojde-li k úhradě, služba po jeho skončení přestane ceny aktualizovat; data zůstávají zachována.",
                ],
            },
            {
                id: "documents",
                heading: "6. Doklady a související dokumenty",
                paragraphs: [
                    "Ke každé automaticky stržené platbě je vystaven daňový doklad se stejnými náležitostmi jako u ruční platby a je k dispozici ke stažení v aplikaci.",
                ],
                bullets: [
                    "Tyto podmínky: /recurring-terms",
                    "Popis předplatného a ceník: /subscription",
                    "Obchodní podmínky: /terms",
                    "Ochrana osobních údajů: /privacy",
                ],
            },
        ],
    },


    privacy: {
        title: "Ochrana osobních údajů",
        effectiveDate: PRIVACY_EFFECTIVE_DATE,
        intro: [
            `Tyto zásady popisují, jak společnost ${COMPANY.legalName} zpracovává osobní údaje při poskytování služby ${COMPANY.brandName}, v souladu s nařízením (EU) 2016/679 (GDPR).`,
        ],
        sections: [
            {
                id: "controller",
                heading: "1. Správce údajů",
                definitions: [
                    { term: "Správce", value: `${COMPANY.legalName}, IČO ${COMPANY.ico}` },
                    { term: "Sídlo", value: formattedAddress("cs") },
                    { term: "Kontakt", value: COMPANY.email },
                ],
                paragraphs: [
                    "Ve vztahu k údajům o rezervacích, které služba načítá z hotelového systému uživatele, vystupuje poskytovatel jako zpracovatel; správcem zůstává uživatel jako provozovatel ubytovacího zařízení.",
                ],
            },
            {
                id: "data",
                heading: "2. Zpracovávané údaje",
                bullets: [
                    "Údaje o účtu: e-mailová adresa, jméno, telefonní číslo zadané při registraci, přihlašovací identifikátory.",
                    "Fakturační údaje: obchodní firma, sídlo, IČO, DIČ, historie plateb a vystavených dokladů.",
                    "Provozní údaje: záznamy o přihlášení, IP adresa, technické protokoly a chybová hlášení.",
                    "Data z hotelového systému: rezervace, obsazenost a ceny — zpracovávaná pro výpočet doporučených cen.",
                    "Konverzace s asistentem Optiva: otázky napsané v okně „Zeptat se Optiva“, odpovědi asistenta, údaj o obrazovce aplikace, ze které byla otázka položena, vaše hodnocení odpovědí a záznam o tom, které údaje o objektu asistent k odpovědi načetl (bez načtených údajů samotných).",
                ],
            },
            {
                id: "purposes",
                heading: "3. Účely a právní základ",
                bullets: [
                    "Poskytování služby a správa účtu, včetně telefonátu k aktivaci nově registrovaného účtu — plnění smlouvy.",
                    "Vyúčtování, vystavování daňových dokladů a jejich archivace — plnění právní povinnosti.",
                    "Zabezpečení služby, prevence zneužití a technická podpora — oprávněný zájem.",
                    "Provozní sdělení o změnách služby — oprávněný zájem; obchodní sdělení pouze se souhlasem.",
                    "Okamžité odpovědi asistenta Optiva na dotazy k nastavení a cenám objektu a předání konverzace podpoře, požádáte-li o to — plnění smlouvy.",
                    "Hodnocení odpovědí asistenta Optiva, které zadáte, a jeho vyhodnocení za účelem zlepšování odpovědí — oprávněný zájem.",
                ],
            },
            {
                id: "processors",
                heading: "4. Zpracovatelé a příjemci",
                paragraphs: [
                    "Poskytovatel využívá tyto zpracovatele, kteří údaje zpracovávají výhradně podle jeho pokynů:",
                ],
                bullets: [
                    "GoPay s.r.o. — zpracování plateb platební kartou.",
                    "DigitalOcean, LLC — provoz aplikace a databáze (datové centrum v EU).",
                    "Logto — správa uživatelských identit a přihlašování.",
                    "Sentry — sběr technických chybových hlášení; nastaveno tak, aby nepřenášelo IP adresy, cookies, hlavičky ani obsah požadavků, a bez záznamu obrazovky.",
                    "Mailgun — odesílání transakčních e-mailů.",
                    "Anthropic, PBC (USA) — jazykový model Claude, který generuje odpovědi asistenta Optiva. Předává se mu text otázky, údaj o obrazovce aplikace, předchozí otázky a odpovědi téže konverzace a údaje o objektu, které asistent k odpovědi načte: nastavení cenové strategie, ceny, obsazenost a souhrnné počty rezervací. Jednotlivé rezervace, jména ani kontaktní údaje hostů mu Optivo nepředává. Anthropic údaje zpracovává v USA (bod 5) a nepoužívá je k trénování svých modelů.",
                ],
            },
            {
                id: "assistant",
                heading: "5. Asistent Optiva",
                paragraphs: [
                    "Asistent Optiva, dostupný v okně „Zeptat se Optiva“, je systém umělé inteligence. Odpovídá automaticky pomocí jazykového modelu a jeho odpovědi mohou být nepřesné. Údaje o objektu pouze čte a sám nemění žádná nastavení ani ceny; podporu Optiva nenahrazuje a konverzaci jí můžete kdykoli předat.",
                    `Odpovědi asistenta generuje společnost Anthropic, PBC v USA. Předání údajů do USA se opírá o standardní smluvní doložky přijaté Evropskou komisí (prováděcí rozhodnutí Komise (EU) 2021/914). Jejich kopii vám poskytneme na žádost zaslanou na ${COMPANY.email}.`,
                    "Do otázek ani do poznámky, s níž předáváte konverzaci podpoře, nepište jména ani kontaktní údaje hostů. Otázku či poznámku, v níž rozpozná e-mailovou adresu, telefonní číslo nebo číslo platební karty či bankovního účtu, služba odmítne dříve, než ji uloží nebo předá. Nedokáže však spolehlivě rozpoznat každý zápis takových údajů ani jméno; co nerozpozná, zpracuje se stejně jako ostatní text otázky, a proto je do otázek nepište.",
                ],
            },
            {
                id: "retention",
                heading: "6. Doba uchování",
                paragraphs: [
                    "Údaje o účtu uchováváme po dobu trvání smlouvy a následně 3 roky. Účetní a daňové doklady uchováváme po dobu stanovenou právními předpisy, tedy zpravidla 10 let. Provozní protokoly uchováváme nejvýše 12 měsíců.",
                    "Konverzace s asistentem Optiva mažeme 90 dní po poslední zprávě a se smazáním účtu. Konverzaci předanou podpoře uchováváme s dotazem, k němuž byla předána, a mažeme ji 12 měsíců po vyřízení dotazu.",
                ],
            },
            {
                id: "rights",
                heading: "7. Práva subjektu údajů",
                bullets: [
                    "právo na přístup k údajům a na jejich kopii,",
                    "právo na opravu nepřesných údajů,",
                    "právo na výmaz a na omezení zpracování,",
                    "právo na přenositelnost údajů,",
                    "právo vznést námitku proti zpracování z titulu oprávněného zájmu,",
                    "právo odvolat udělený souhlas.",
                ],
                paragraphs: [
                    `Práva uplatníte e-mailem na ${COMPANY.email}. Máte rovněž právo podat stížnost u Úřadu pro ochranu osobních údajů, Pplk. Sochora 27, 170 00 Praha 7, uoou.cz.`,
                ],
            },
            {
                id: "cookies",
                heading: "8. Cookies a úložiště prohlížeče",
                paragraphs: [
                    "Aplikace používá pouze technické cookies nezbytné pro přihlášení a udržení relace a předvolby rozhraní uložené ve vašem prohlížeči. Nepoužíváme reklamní ani sledovací cookies třetích stran a nepoužíváme nástroje pro záznam obrazovky.",
                    "Ukládáme pouze toto:",
                ],
                bullets: [
                    "Relační cookie přihlášení (Logto) — bez ní se nelze přihlásit.",
                    "optivo-ui-theme — zvolený světlý nebo tmavý režim.",
                    "optivo-ui-skin — zvolený vzhled rozhraní.",
                    "optivo-ui-language — zvolený jazyk.",
                    "optivo-ui-drafts-cell-metric — zvolený údaj zobrazený v buňkách cenového kalendáře.",
                    "optivo-cookie-notice-ack — potvrzení, že jste si přečetli informaci o úložišti, aby se nezobrazovala znovu.",
                ],
                notes: [
                    "Všechny uvedené položky jsou nezbytné pro provoz služby nebo je ukládáme na váš pokyn, když si nastavíte rozhraní. Nepodléhají proto souhlasu podle § 89 odst. 3 zákona č. 127/2005 Sb. a nenabízíme jejich odmítnutí — nebylo by co odmítnout. Smazat je můžete kdykoli v nastavení svého prohlížeče.",
                ],
            },
            {
                id: "contact",
                heading: "9. Kontakt",
                paragraphs: [
                    `S dotazy ke zpracování osobních údajů se obraťte na ${COMPANY.email} nebo na adresu sídla. Tyto zásady jsou účinné od ${PRIVACY_EFFECTIVE_DATE}.`,
                ],
            },
        ],
    },
};

// ---------------------------------------------------------------------------
// English
// ---------------------------------------------------------------------------

const EN: Record<OwnDocumentKey, LegalDoc> = {

    contact: {
        title: "Contact details",
        effectiveDate: EFFECTIVE_DATE,
        intro: [
            `The ${COMPANY.brandName} service is operated by ${legalNameAtSentenceEnd()} Full identification and contact details are set out below.`,
        ],
        sections: [
            {
                id: "identification",
                heading: "Identification",
                definitions: [
                    { term: "Company", value: COMPANY.legalName },
                    { term: "Registered seat", value: formattedAddress("en") },
                    { term: "Company ID (IČO)", value: COMPANY.ico },
                    { term: "VAT ID (DIČ)", value: dicLine("en").replace("VAT ID: ", "") },
                    { term: "Commercial register", value: COMPANY.registration },
                    { term: "Website", value: COMPANY.website },
                ],
            },
            {
                id: "contact",
                heading: "Get in touch",
                definitions: [
                    { term: "E-mail", value: COMPANY.email },
                    { term: "Phone", value: COMPANY.phone },
                    { term: "Support hours", value: COMPANY.supportHoursEn },
                ],
                paragraphs: [
                    "We answer e-mail enquiries within two business days as a rule. The registered seat is also the postal address for complaints and withdrawal notices.",
                ],
            },
            {
                id: "billing",
                heading: "Billing and bank details",
                definitions: [
                    { term: "Billing e-mail", value: COMPANY.billingEmail },
                    { term: "Bank", value: COMPANY.bankName },
                    { term: "Account number", value: COMPANY.bankAccount },
                    { term: "IBAN", value: COMPANY.iban },
                    { term: "BIC/SWIFT", value: COMPANY.bic },
                ],
                paragraphs: [
                    "Please mark bank transfers with the variable symbol shown next to the payment in the application. Tax documents can be downloaded directly in the application.",
                ],
            },
            {
                id: "complaints",
                heading: "Complaints and withdrawal",
                paragraphs: [
                    `Complaints and withdrawal notices are accepted at ${COMPANY.email} or in writing at the registered seat. Complaints are settled within 30 days. The full procedure, including what the provider warrants and when a complaint cannot be upheld, is set out in the complaints procedure at /complaints.`,
                ],
            },
            {
                id: "documents",
                heading: "Related documents",
                bullets: [
                    "Terms and conditions: /terms",
                    "Complaints procedure: /complaints",
                    "Privacy notice: /privacy",
                ],
            },
        ],
    },

    recurring: {
        title: "Recurring payment terms",
        effectiveDate: RECURRING_EFFECTIVE_DATE,
        intro: [
            `This document describes how consent to recurring card payments arises for ${COMPANY.brandName}, how the payments are taken, and how to cancel them. It supplements the terms and conditions at /terms and concerns automatic renewal of the subscription only.`,
        ],
        sections: [
            {
                id: "origination",
                heading: "1. How consent arises",
                paragraphs: [
                    "Recurring payments never start on their own. They begin only when you explicitly tick automatic renewal while paying by card. Without that tick, the payment is a one-off and nothing further is charged.",
                    "The first payment you confirm in the gateway is itself the granting of the mandate for the payments that follow. Before you submit it, the application states the amount, the frequency and the date of the next payment.",
                    "Consent is given by the cardholder inside the GoPay payment gateway. The card number is stored by the gateway; the provider has no access to it and does not store it.",
                ],
            },
            {
                id: "amount",
                heading: "2. Amount and frequency",
                bullets: [
                    "Frequency: according to the period you chose — once a month on a monthly subscription, once a year on a yearly one, always on the day the paid period ends.",
                    "Yearly subscription: twelve months paid at once, at the same monthly price as a monthly one.",
                    "Currency: CZK.",
                ],
                notes: [
                    "This is a variable-amount payment: the price follows the properties the subscription covers, the number of their rooms and any individually agreed services, so it can change from one period to the next. That is why the mandate is registered with the gateway as an on-demand payment rather than a fixed standing order.",
                    "The amount for the coming period is always shown in the Billing section of the application before it is charged.",
                ],
            },
            {
                id: "duration",
                heading: "3. How long consent lasts",
                paragraphs: [
                    "Consent is granted for five years from the first payment, or until it is cancelled earlier. After that the payments stop by themselves and consent has to be given again.",
                    "Consent also ends when the subscription is cancelled, when the card expires, or when it is blocked by its issuer.",
                ],
            },
            {
                id: "cancellation",
                heading: "4. How to cancel recurring payments",
                paragraphs: [
                    "You can cancel automatic renewal at any time in the Billing section of the application, using the “Cancel automatic renewal” button. Cancellation is immediate and free of charge — the mandate is revoked at the payment gateway as well, so no further payment can be taken from the card.",
                    `Cancellation can also be sent by e-mail to ${COMPANY.email}; we action it by the next business day at the latest.`,
                    "Cancelling automatic renewal does not cancel a period already paid for — that runs to its end. Only future automatic payments are stopped.",
                ],
            },
            {
                id: "failure",
                heading: "5. Failed payments",
                paragraphs: [
                    "If a payment fails (insufficient funds, an expired or blocked card), the subscription is not extended and the reason is shown in the application. We do not revoke the mandate — a card may work again the next day — but neither do we retry automatically or repeatedly without your knowledge.",
                    "The period already paid for runs to its end. If payment is not made, the service stops updating rates after it expires; your data is retained.",
                ],
            },
            {
                id: "documents",
                heading: "6. Documents and related pages",
                paragraphs: [
                    "A tax document with the same particulars as for a manual payment is issued for every automatically charged payment and can be downloaded in the application.",
                ],
                bullets: [
                    "These terms: /recurring-terms",
                    "Subscription description and price list: /subscription",
                    "Terms and conditions: /terms",
                    "Privacy notice: /privacy",
                ],
            },
        ],
    },


    privacy: {
        title: "Personal data protection",
        effectiveDate: PRIVACY_EFFECTIVE_DATE,
        intro: [
            `This notice describes how ${COMPANY.legalName} processes personal data when providing the ${COMPANY.brandName} service, in accordance with Regulation (EU) 2016/679 (GDPR).`,
        ],
        sections: [
            {
                id: "controller",
                heading: "1. Controller",
                definitions: [
                    { term: "Controller", value: `${COMPANY.legalName}, Company ID ${COMPANY.ico}` },
                    { term: "Registered seat", value: formattedAddress("en") },
                    { term: "Contact", value: COMPANY.email },
                ],
                paragraphs: [
                    "For the booking data the service reads from the user's property management system, the provider acts as a processor; the user, as the operator of the accommodation, remains the controller.",
                ],
            },
            {
                id: "data",
                heading: "2. Data processed",
                bullets: [
                    "Account data: e-mail address, name, the phone number given at registration, sign-in identifiers.",
                    "Billing data: company name, seat, company ID, VAT ID, payment and document history.",
                    "Operational data: sign-in records, IP address, technical logs and error reports.",
                    "Property management system data: bookings, occupancy and rates — processed to calculate recommended prices.",
                    "Conversations with the Optivo assistant: the questions typed in the Ask Optivo window, the assistant's answers, the application screen the question was asked from, your ratings of the answers, and a record of which property data the assistant read to answer (without the data read).",
                ],
            },
            {
                id: "purposes",
                heading: "3. Purposes and legal bases",
                bullets: [
                    "Providing the service and administering the account, including a call to activate a newly registered account — performance of a contract.",
                    "Billing, issuing tax documents and archiving them — compliance with a legal obligation.",
                    "Securing the service, preventing misuse and technical support — legitimate interest.",
                    "Operational notices about changes to the service — legitimate interest; marketing messages only with consent.",
                    "Instant answers from the Optivo assistant to questions about the property's settings and prices, and handing the conversation to support when you ask — performance of a contract.",
                    "Your ratings of the assistant's answers and their evaluation to improve the answers — legitimate interest.",
                ],
            },
            {
                id: "processors",
                heading: "4. Processors and recipients",
                paragraphs: [
                    "The provider uses the following processors, which process data solely on the provider's instructions:",
                ],
                bullets: [
                    "GoPay s.r.o. — card payment processing.",
                    "DigitalOcean, LLC — application and database hosting (EU data centre).",
                    "Logto — user identity and sign-in management.",
                    "Sentry — collection of technical error reports; configured to transmit no IP address, cookies, request headers or request content, and to record no screen content.",
                    "Mailgun — delivery of transactional e-mail.",
                    "Anthropic, PBC (USA) — the Claude language model, which generates the Optivo assistant's answers. It receives the text of the question, the application screen it was asked from, the earlier questions and answers of the same conversation, and the property data the assistant reads to answer: the pricing strategy's settings, prices, occupancy and aggregate counts of bookings. Optivo does not pass it individual bookings, guests' names or contact details. Anthropic processes the data in the USA (section 5) and does not use it to train its models.",
                ],
            },
            {
                id: "assistant",
                heading: "5. The Optivo assistant",
                paragraphs: [
                    "The Optivo assistant, offered in the Ask Optivo window, is an AI system. It answers automatically using a language model, and its answers may be inaccurate. It only reads the property's data and itself changes no settings and no prices; it does not replace Optivo support, and you can hand the conversation to support at any time.",
                    `The assistant's answers are generated by Anthropic, PBC in the USA. The transfer of data to the USA relies on the standard contractual clauses adopted by the European Commission (Commission Implementing Decision (EU) 2021/914). A copy of them is available on request to ${COMPANY.email}.`,
                    "Do not write guests' names or contact details in your questions or in the note with which you hand a conversation to support. The service refuses a question or note in which it recognises an e-mail address, a telephone number, or a payment card or bank account number before it is stored or passed on. It cannot reliably recognise every way such details are written, nor a name; whatever it does not recognise is processed like the rest of the question, which is why you must not write them.",
                ],
            },
            {
                id: "retention",
                heading: "6. Retention",
                paragraphs: [
                    "Account data is kept for the duration of the contract and for 3 years afterwards. Accounting and tax documents are kept for the period required by law, as a rule 10 years. Operational logs are kept for at most 12 months.",
                    "Conversations with the Optivo assistant are deleted 90 days after the last message, and when the account is deleted. A conversation handed to support is kept with the request it was handed over as, and deleted 12 months after the request is closed.",
                ],
            },
            {
                id: "rights",
                heading: "7. Your rights",
                bullets: [
                    "the right of access to your data and to a copy of it,",
                    "the right to rectification of inaccurate data,",
                    "the right to erasure and to restriction of processing,",
                    "the right to data portability,",
                    "the right to object to processing based on legitimate interest,",
                    "the right to withdraw consent already given.",
                ],
                paragraphs: [
                    `To exercise these rights, write to ${COMPANY.email}. You also have the right to lodge a complaint with the Office for Personal Data Protection, Pplk. Sochora 27, 170 00 Prague 7, uoou.cz.`,
                ],
            },
            {
                id: "cookies",
                heading: "8. Cookies and browser storage",
                paragraphs: [
                    "The application uses only technical cookies necessary for sign-in and session handling, plus interface preferences stored in your browser. No advertising or third-party tracking cookies are used, and no session-recording tool is loaded.",
                    "This is everything that is stored:",
                ],
                bullets: [
                    "The sign-in session cookie (Logto) — without it you cannot sign in.",
                    "optivo-ui-theme — your choice of light or dark mode.",
                    "optivo-ui-skin — your choice of interface skin.",
                    "optivo-ui-language — your choice of language.",
                    "optivo-ui-drafts-cell-metric — the metric you chose to show in pricing calendar cells.",
                    "optivo-cookie-notice-ack — a record that you have read the storage notice, so it is not shown again.",
                ],
                notes: [
                    "Every item listed is either strictly necessary to run the service or stored at your own request when you configure the interface. None is therefore subject to consent under the ePrivacy Directive, and no reject option is offered — there would be nothing to reject. You can delete all of it at any time in your browser settings.",
                ],
            },
            {
                id: "contact",
                heading: "9. Contact",
                paragraphs: [
                    `For questions about the processing of personal data, write to ${COMPANY.email} or to the registered seat. This notice is effective from ${PRIVACY_EFFECTIVE_DATE}.`,
                ],
            },
        ],
    },
};

/**
 * The documents in force. The terms and their specification are counsel's
 * (`vop/`); the price list, the recurring terms, the complaints procedure and
 * the contact page restate them, the first three derived from the pages
 * written here so that a sentence both say is written once.
 */
function documentsFor(lang: LegalLanguage, own: Record<OwnDocumentKey, LegalDoc>): Record<LegalDocumentKey, LegalDoc> {
    const cs = lang === "cs";
    return {
        terms: cs ? VOP_TERMS_CS : VOP_TERMS_EN,
        specification: cs ? VOP_SPECIFICATION_CS : VOP_SPECIFICATION_EN,
        contact: buildVopContact(lang, own.contact),
        privacy: own.privacy,
        subscription: cs ? VOP_PRICE_LIST_CS : VOP_PRICE_LIST_EN,
        payment: buildVopPayment(lang),
        recurring: buildVopRecurring(lang, own.recurring),
        complaints: buildVopComplaints(lang),
        dpa: cs ? DPA_CS : DPA_EN,
    };
}

const DOCUMENTS: Record<LegalLanguage, Record<LegalDocumentKey, LegalDoc>> = {
    cs: documentsFor("cs", CS),
    en: documentsFor("en", EN),
};

export function legalDocumentsFor(lang: LegalLanguage): Record<LegalDocumentKey, LegalDoc> {
    return DOCUMENTS[lang];
}

export function legalDocument(key: LegalDocumentKey, lang: LegalLanguage): LegalDoc {
    return DOCUMENTS[lang][key];
}

/**
 * Public paths of each document. Both languages get a path, because the URL
 * is handed to a Czech payment gateway and read by Czech-speaking operators,
 * while the English one is what an international reviewer expects.
 */
export const LEGAL_ROUTES: Record<string, LegalDocumentKey> = {
    "/terms": "terms",
    "/obchodni-podminky": "terms",
    "/specification": "specification",
    "/specifikace": "specification",
    "/contact": "contact",
    "/kontakt": "contact",
    "/privacy": "privacy",
    "/ochrana-osobnich-udaju": "privacy",
    "/subscription": "subscription",
    "/predplatne": "subscription",
    "/cenik": "subscription",
    "/payment": "payment",
    "/platba": "payment",
    "/recurring-terms": "recurring",
    "/podminky-opakovanych-plateb": "recurring",
    "/complaints": "complaints",
    "/reklamacni-rad": "complaints",
    "/dpa": "dpa",
    "/zpracovani-udaju": "dpa",
};

/** The canonical (English) path of a document, used for cross-links. */
export const LEGAL_CANONICAL_PATH: Record<LegalDocumentKey, string> = {
    terms: "/terms",
    specification: "/specification",
    contact: "/contact",
    privacy: "/privacy",
    subscription: "/subscription",
    payment: "/payment",
    recurring: "/recurring-terms",
    complaints: "/complaints",
    dpa: "/dpa",
};

/**
 * The order the documents are offered in, wherever a surface lists them.
 *
 * Two places already named these documents and neither matched: the welcome
 * footer picks five through its own i18n keys, and `LegalPage` hardcoded five
 * more as bilingual string literals inline — both omitting the subscription
 * and recurring-payment terms, which are exactly the two an operator reads
 * before subscribing. So the list is here, once, ordered by what somebody
 * asking "what am I agreeing to?" wants: the terms first, then what the
 * subscription is and how a card mandate works, then the two consumer-rights
 * documents, then the data ones, with the contact details last.
 *
 * The welcome page keeps its own labels on purpose: it is the public
 * marketing footer the payment gateway reviews, and it names one document
 * "Pricing" rather than by its title.
 */
export const LEGAL_DOCUMENT_ORDER: readonly LegalDocumentKey[] = [
    "terms",
    "specification",
    "subscription",
    "payment",
    "recurring",
    "complaints",
    "privacy",
    "dpa",
    "contact",
];

export interface LegalDocumentLink {
    key: LegalDocumentKey;
    /** The canonical path — always resolvable by `resolveLegalRoute`. */
    path: string;
    /** Read off the document itself, so a renamed one cannot be listed under its old name. */
    title: string;
}

/** Every legal document, in offer order, named as it names itself. */
export function legalDocumentLinks(lang: LegalLanguage): LegalDocumentLink[] {
    return LEGAL_DOCUMENT_ORDER.map((key) => ({
        key,
        path: LEGAL_CANONICAL_PATH[key],
        title: legalDocument(key, lang).title,
    }));
}

/**
 * The number an article carries in its heading ("11. Bezvadnost Služby a
 * vady" is 11), or null for a section that is not numbered. A clause is
 * numbered after its article, so the page and the tests read it here.
 */
export function articleNumber(section: LegalSection): number | null {
    const match = section.heading.match(/^(\d+)\./);
    return match ? Number(match[1]) : null;
}

/** Which legal document a pathname addresses, if any. */
export function resolveLegalRoute(pathname: string): LegalDocumentKey | null {
    const normalized = pathname.toLowerCase().replace(/\/+$/, "") || "/";
    return LEGAL_ROUTES[normalized] ?? null;
}
